import { motion, AnimatePresence } from "framer-motion";  // kept for mobile drawer
import { 
  LayoutGrid, BookOpen, Layers, HelpCircle, Settings, LogOut, Menu, X, User, Users, 
  PlusCircle, BarChart3, Eye, EyeOff, Award, ClipboardList,
  ChevronsLeft, ChevronsRight, Sun, Moon, Globe, Route, FileText,
} from "lucide-react";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/hooks/use-theme";
import { pagePrefetchers } from "@/App";

interface NavItem {
  icon: React.ElementType;
  labelKey: string;
  href: string;
  adminOnly?: boolean;
  managerOnly?: boolean;
}

export function OrgSidebar() {
  const { signOut } = useAuth();
  const { organization, isManager, isAdmin, isSuperAdmin, realIsSuperAdmin, effectiveRole, setTestRole, clearTestRole } = useOrganization();
  const { t, language, setLanguage } = useLanguage();
  const { resolvedTheme, setTheme } = useTheme();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(1024);
  const [isOpen, setIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("sidebar-collapsed") === "true";
    }
    return false;
  });

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", String(next));
    window.dispatchEvent(new Event("sidebar-toggle"));
  };

  const learnerNavItems: NavItem[] = [
    { icon: LayoutGrid, labelKey: "nav.dashboard", href: `/${orgSlug}` },
    { icon: BookOpen, labelKey: "nav.courses", href: `/${orgSlug}/courses` },
    { icon: Award, labelKey: "nav.certificates", href: `/${orgSlug}/certificates` },
  ];

  const managerNavItems: NavItem[] = [
    { icon: LayoutGrid, labelKey: "nav.dashboard", href: `/${orgSlug}` },
    { icon: BookOpen, labelKey: "nav.courses", href: `/${orgSlug}/courses` },
    { icon: Award, labelKey: "nav.certificates", href: `/${orgSlug}/certificates` },
    { icon: Users, labelKey: "nav.users", href: `/${orgSlug}/manage/users`, managerOnly: true },
    { icon: ClipboardList, labelKey: "nav.onboarding", href: `/${orgSlug}/onboarding`, managerOnly: true },
    { icon: PlusCircle, labelKey: "nav.manageCourses" as any, href: `/${orgSlug}/manage/courses`, adminOnly: true },
    
    { icon: BarChart3, labelKey: "nav.analytics", href: `/${orgSlug}/analytics`, managerOnly: true },
    
    { icon: Settings, labelKey: "nav.settings", href: `/${orgSlug}/settings` },
  ];

  const navItems = isManager ? managerNavItems.filter(item => !item.adminOnly || isAdmin) : learnerNavItems;

  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const sidebarWidth = collapsed && !isMobile ? "w-[72px]" : "w-64";

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {!isMobile && (
        <div className={cn("pt-5 pb-3", collapsed ? "px-3" : "px-6")}>
          <div className="flex items-center justify-center">
            {collapsed ? (
              <img src="/trainly-dark.png" alt="Trainly" className="h-5 dark:hidden object-contain" />
            ) : (
              <>
                <img src="/trainly-dark.png" alt="Trainly" className="h-6 dark:hidden" />
                <img src="/trainly-light.png" alt="Trainly" className="h-6 hidden dark:block" />
              </>
            )}
          </div>
        </div>
      )}

      <div className={cn("flex items-center justify-between py-3", !isMobile && "border-t border-border", collapsed && !isMobile ? "px-3 justify-center" : "px-6")}>
        <div className="flex items-center gap-3">
          {organization?.logo_url ? (
            <img 
              src={organization.logo_url} 
              alt={organization.name}
              className={cn("rounded-lg object-cover shrink-0", collapsed && !isMobile ? "h-8 w-8" : "h-10 w-10")}
            />
          ) : (
            <div className={cn("rounded-lg bg-primary/10 flex items-center justify-center shrink-0", collapsed && !isMobile ? "h-8 w-8" : "h-10 w-10")}>
              <span className={cn("font-bold text-primary", collapsed && !isMobile ? "text-sm" : "text-base")}>
                {organization?.name?.charAt(0) || "O"}
              </span>
            </div>
          )}
          {(!collapsed || isMobile) && (
            <span className="font-semibold text-foreground">
              {organization?.name || "Organization"}
            </span>
          )}
        </div>
        {isMobile && (
          <button 
            onClick={() => setIsOpen(false)} 
            className="p-2 hover:bg-muted rounded-xl transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        )}
      </div>

      <div className={cn("flex-1 mt-4", collapsed && !isMobile ? "px-2" : "px-4")}>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const isActive = item.href === `/${orgSlug}` 
              ? location.pathname === item.href 
              : location.pathname.startsWith(item.href);
            const Icon = item.icon;
            
            // Derive prefetch key from href (strip /:orgSlug/ prefix)
            const prefetchKey = item.href === `/${orgSlug}`
              ? "dashboard"
              : item.href.replace(`/${orgSlug}/`, "");
            const prefetch = () => {
              const loader = pagePrefetchers[prefetchKey];
              if (loader) loader().catch(() => {});
            };

            return (
              <button
                key={item.labelKey}
                onMouseEnter={prefetch}
                onFocus={prefetch}
                onTouchStart={prefetch}
                onClick={() => {
                  navigate(item.href);
                  if (isMobile) setIsOpen(false);
                }}
                title={collapsed && !isMobile ? t(item.labelKey as any) : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl py-3 text-sm font-medium transition-all text-left",
                  collapsed && !isMobile ? "justify-center px-2" : "px-4",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {(!collapsed || isMobile) && <span>{t(item.labelKey as any)}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {realIsSuperAdmin && (!collapsed || isMobile) && (
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("nav.viewAs")}</span>
          </div>
          <Select
            value={effectiveRole || "super_admin"}
            onValueChange={(value) => {
              if (value === "super_admin") {
                clearTestRole();
              } else {
                setTestRole(value as "admin" | "manager" | "learner");
              }
            }}
          >
            <SelectTrigger className="w-full h-9 rounded-xl text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="learner">Learner</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className={cn("py-4 border-t border-border mt-auto", collapsed && !isMobile ? "px-2" : "px-4")}>
        {/* Language & Theme toggles */}
        <div className={cn("flex items-center mb-2", collapsed && !isMobile ? "flex-col gap-1" : "gap-1 justify-center")}>
          <button
            onClick={() => setLanguage(language === "no" ? "en" : "no")}
            title={t("nav.switchLang" as any)}
            className={cn(
              "flex items-center gap-2 rounded-xl py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
              collapsed && !isMobile ? "justify-center px-2" : "px-3"
            )}
          >
            <Globe className="h-4 w-4 shrink-0" />
            {(!collapsed || isMobile) && <span>{language === "no" ? "EN" : "NO"}</span>}
          </button>
          <button
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            title={resolvedTheme === "dark" ? t("nav.lightTheme" as any) : t("nav.darkTheme" as any)}
            className={cn(
              "flex items-center gap-2 rounded-xl py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
              collapsed && !isMobile ? "justify-center px-2" : "px-3"
            )}
          >
            {resolvedTheme === "dark" ? <Sun className="h-4 w-4 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
            {(!collapsed || isMobile) && <span>{resolvedTheme === "dark" ? t("nav.lightTheme" as any) : t("nav.darkTheme" as any)}</span>}
          </button>
        </div>

        <nav className="space-y-1">
          <button
            onClick={() => {
              navigate(`/${orgSlug}/profile`);
              if (isMobile) setIsOpen(false);
            }}
            title={collapsed && !isMobile ? t("nav.profile") : undefined}
            className={cn(
              "flex w-full items-center gap-3 rounded-xl py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-left",
              collapsed && !isMobile ? "justify-center px-2" : "px-4"
            )}
          >
            <User className="h-5 w-5 shrink-0" />
            {(!collapsed || isMobile) && <span>{t("nav.profile")}</span>}
          </button>
          <button
            onClick={handleSignOut}
            title={collapsed && !isMobile ? t("nav.signOut") : undefined}
            className={cn(
              "flex w-full items-center gap-3 rounded-xl py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
              collapsed && !isMobile ? "justify-center px-2" : "px-4"
            )}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {(!collapsed || isMobile) && <span>{t("nav.signOut")}</span>}
          </button>
        </nav>

        {/* Collapse toggle - desktop only */}
        {!isMobile && (
          <button
            onClick={toggleCollapsed}
            className="flex w-full items-center justify-center gap-2 mt-2 py-2 rounded-xl text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            {!collapsed && <span>{t("nav.hideMenu" as any)}</span>}
          </button>
        )}
      </div>
    </div>
  );

  const MobileTopBar = () => (
    <div className="fixed top-0 left-0 right-0 z-50 lg:hidden flex items-center justify-between px-4 py-3 bg-card/70 backdrop-blur-xl border-b border-border/50">
      <button
        onClick={() => navigate(`/${orgSlug}`)}
        className="flex items-center"
      >
        <img src="/trainly-dark.png" alt="Trainly" className="h-6 dark:hidden" />
        <img src="/trainly-light.png" alt="Trainly" className="h-6 hidden dark:block" />
      </button>
      <div className="flex items-center gap-2">
        <NotificationBell />
        <motion.button
          onClick={() => setIsOpen(prev => !prev)}
          className="p-2.5 bg-muted rounded-xl"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Menu className="h-5 w-5 text-foreground" />
        </motion.button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileTopBar />
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              />
              <motion.aside
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="fixed left-0 top-0 z-50 flex h-screen w-64 flex-col bg-card shadow-2xl"
              >
                {sidebarContent}
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <aside
      style={{ width: collapsed ? 72 : 256 }}
      className="fixed left-0 top-0 z-40 hidden lg:flex h-screen flex-col bg-card/80 backdrop-blur-xl border-r border-border/50 transition-[width] duration-200 ease-in-out"
    >
      {sidebarContent}
    </aside>
  );
}
